// src/components/Login.jsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { loginDriver } from "../api/auth";
import LegalDocsModal from "./LegalDocsModal.jsx";
import {
  fetchUzLegalBundle,
  findLegalDocument,
  legalDocumentUrl,
} from "../services/legalV5.js";
import LanguageSelector from "./LanguageSelector.jsx";

export default function Login({ onLogin, onRegister }) {
  const { t, i18n } = useTranslation();

  const [login, setLogin] = useState(() => {
    try {
      return localStorage.getItem("driver_login_hint") || "";
    } catch {
      return "";
    }
  });

  const [password, setPassword] = useState("");
  const [showOffer, setShowOffer] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [legalReaccept, setLegalReaccept] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [reacceptDoc, setReacceptDoc] = useState(null);
  const [publicLegalBundle, setPublicLegalBundle] = useState(null);

  const offerUrl = legalDocumentUrl(findLegalDocument(publicLegalBundle, "REISOR-UA-001"));
  const privacyUrl = legalDocumentUrl(findLegalDocument(publicLegalBundle, "REISOR-PRIVACY-001"));

  useEffect(() => {
    let active = true;
    fetchUzLegalBundle("driver", i18n?.language || "ru")
      .then((bundle) => { if (active) setPublicLegalBundle(bundle); })
      .catch(() => { if (active) setPublicLegalBundle(null); });
    return () => { active = false; };
  }, [i18n?.language]);

  useEffect(() => {
    try {
      const digits = String(login || "").replace(/\D/g, "");
      if (digits) localStorage.setItem("driver_login_hint", digits);
    } catch {}
  }, [login]);

  const handleLogin = async () => {
    if (loginBusy) return;
    setLoginError("");
    const loginDigits = String(login || "").replace(/\D/g, "");
    const passDigits = String(password || "").replace(/\D/g, "");

    if (!loginDigits || !passDigits) {
      setLoginError(t("login.alerts.need_phone_pass"));
      return;
    }

    if (loginDigits.length < 7 || loginDigits.length > 15) {
      setLoginError(t("login.alerts.need_full_phone"));
      return;
    }

    if (passDigits.length !== 6) {
      setLoginError(t("login.alerts.pass_6_digits"));
      return;
    }

    if (legalReaccept && (!agreementAccepted || !personalDataConsent)) {
      setLoginError(t("login.legalReacceptRequired", {
        defaultValue: "Чтобы войти, примите обновлённые условия REISOR и отдельное согласие на обработку персональных данных.",
      }));
      return;
    }

    try {
      setLoginBusy(true);
      const payload = legalReaccept
        ? {
            login: loginDigits,
            password: passDigits,
            agreementAccepted,
            personalDataConsent,
            legalLocale: legalReaccept.locale || "ru",
            presentedBundle: legalReaccept.presentedBundle,
          }
        : { login: loginDigits, password: passDigits };
      const res = await loginDriver(payload);

      const driverObj = res?.driver || res?.user || res?.data?.driver || null;
      const driverId =
        driverObj?._id ||
        driverObj?.id ||
        driverObj?.driverId ||
        res?.driverId ||
        res?._id ||
        res?.id ||
        "";

      localStorage.setItem("driver_data", JSON.stringify(driverObj || {}));
      localStorage.setItem("driver_id", String(driverId || ""));

      if (res.token) {
        localStorage.setItem("driver_token", res.token);
        try {
          sessionStorage.setItem("driver_token", res.token);
        } catch {}
      }

      try {
        window.dispatchEvent(new Event("gptcargo_driver_auth_changed"));
      } catch {}

      setLegalReaccept(null);
      setAgreementAccepted(false);
      setPersonalDataConsent(false);
      onLogin(res.token, driverObj || res.driver);
    } catch (err) {
      const code = String(err?.code || err?.message || "").trim();

      if (code === "LEGAL_REACCEPT_REQUIRED" || code === "LEGAL_BUNDLE_STALE") {
        const requirement = err?.data?.legal || err?.data?.details?.legal || null;
        if (requirement?.presentedBundle) {
          setLegalReaccept(requirement);
          setAgreementAccepted(false);
          setPersonalDataConsent(false);
          setLoginError(t("login.legalReacceptRequired", {
            defaultValue: "Условия REISOR обновились. Ознакомьтесь с текущими документами и подтвердите их перед входом.",
          }));
          return;
        }
      }

      if (code === "NOT_APPROVED") {
        setLoginError(t("login.alerts.not_approved"));
      } else if (code === "BLOCKED") {
        setLoginError(t("login.alerts.blocked"));
      } else if (code === "PASSWORD_NOT_SET") {
        setLoginError(t("login.alerts.pass_not_set"));
      } else if (code === "LOGIN_REQUIRED") {
        setLoginError(t("login.alerts.need_both"));
      } else if (code === "BAD_API_BASE") {
        setLoginError(t("login.alerts.bad_api", { defaultValue: "Driver APK is not connected to backend API. backend.cargo-platform.online is required." }));
      } else {
        setLoginError(t("login.alerts.bad_login"));
      }
    } finally {
      setLoginBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "radial-gradient(circle at center, #0b1e3f, #000814)",
      }}
    >
      <div
        className="w-full"
        style={{
          maxWidth: "420px",
          background: "rgba(15, 23, 42, 0.9)",
          padding: "28px",
          borderRadius: "28px",
          boxShadow: "0 0 60px rgba(0, 122, 255, 0.25)",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "700",
            color: "#60a5fa",
            marginBottom: "20px",
            lineHeight: "1.2",
          }}
        >
          🚛 REISOR Driver
          <br />
          <span style={{ color: "#e2e8f0", fontSize: "18px", fontWeight: "700" }}>
            {t("app.driver", { defaultValue: "Водитель" })}
          </span>
        </h1>

        <div style={{ marginBottom: "18px" }}>
          <LanguageSelector compact />
        </div>

        <input
          name="username"
          autoComplete="username"
          inputMode="tel"
          enterKeyHint="next"
          type="text"
          placeholder="+7 900 000-00-00"
          value={login}
          maxLength={32}
          onChange={(e) => {
            const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 15);
            setLoginError("");
            setLegalReaccept(null);
            setAgreementAccepted(false);
            setPersonalDataConsent(false);
            setLogin(digits);
          }}
          style={{
            width: "100%",
            padding: "16px",
            marginBottom: "18px",
            borderRadius: "16px",
            border: "1px solid #334155",
            background: "rgba(30, 41, 59, 0.9)",
            color: "white",
            fontSize: "18px",
          }}
        />

        <input
          name="password"
          autoComplete="current-password"
          inputMode="numeric"
          pattern="[0-9]*"
          enterKeyHint="done"
          type="password"
          placeholder={t("login.password")}
          value={password}
          maxLength={6}
          onChange={(e) => {
            setLoginError("");
            setPassword(e.target.value.replace(/\D/g, ""));
          }}
          style={{
            width: "100%",
            padding: "16px",
            marginBottom: "10px",
            borderRadius: "16px",
            border: "1px solid #334155",
            background: "rgba(30, 41, 59, 0.9)",
            color: "white",
            fontSize: "18px",
          }}
        />

        <div
          style={{
            color: "#94a3b8",
            fontSize: "14px",
            marginBottom: "14px",
            lineHeight: "1.4",
            textAlign: "center",
          }}
        >
          {t("login.legalDocuments", { defaultValue: "Юридические документы REISOR:" })}{" "}
          <span
            style={{ color: "#60a5fa", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setShowOffer(true)}
          >
            {t("login.offer", { defaultValue: "Соглашение" })}
          </span>{" "}
          {t("login.and", { defaultValue: "и" })}{" "}
          <span
            style={{ color: "#60a5fa", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setShowPolicy(true)}
          >
            {t("login.privacy", { defaultValue: "Политика конфиденциальности" })}
          </span>.
        </div>

        {legalReaccept ? (
          <div
            data-reisor-legal-reaccept="driver-v1"
            style={{
              marginBottom: "14px",
              borderRadius: "16px",
              border: "1px solid rgba(96, 165, 250, 0.55)",
              background: "rgba(15, 23, 42, 0.96)",
              padding: "14px",
              color: "#e2e8f0",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 8 }}>
              {t("login.legalReacceptTitle", { defaultValue: "Обновлены условия REISOR" })}
            </div>
            <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 10 }}>
              {t("login.legalReacceptHint", { defaultValue: "Перед новым входом ознакомьтесь с текущей редакцией обязательных документов." })}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
              {(legalReaccept.documents || []).map((doc) => (
                <button
                  key={`${doc.documentNumber}:${doc.version}`}
                  type="button"
                  onClick={() => setReacceptDoc(doc)}
                  style={{
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    color: "#60a5fa",
                    textAlign: "left",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  {doc.title || doc.documentNumber} · v{doc.version}
                </button>
              ))}
            </div>

            <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 10, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={agreementAccepted}
                onChange={(e) => { setAgreementAccepted(e.target.checked); setLoginError(""); }}
              />
              <span>{t("login.legalAgreementDecision", { defaultValue: "Принимаю Пользовательское соглашение, применимые правила роли и правила платформы." })}</span>
            </label>

            <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 14 }}>
              <input
                type="checkbox"
                checked={personalDataConsent}
                onChange={(e) => { setPersonalDataConsent(e.target.checked); setLoginError(""); }}
              />
              <span>{t("login.legalPdDecision", { defaultValue: "Отдельно даю согласие на обработку персональных данных." })}</span>
            </label>
          </div>
        ) : null}

        {loginError ? (
          <div
            role="alert"
            aria-live="assertive"
            data-reisor-login-error="inline-v1"
            style={{
              marginBottom: "12px",
              borderRadius: "14px",
              border: "1px solid rgba(248, 113, 113, 0.55)",
              background: "rgba(127, 29, 29, 0.42)",
              color: "#fee2e2",
              padding: "12px 14px",
              fontSize: "15px",
              fontWeight: "700",
              lineHeight: "1.4",
              textAlign: "center",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
            }}
          >
            {loginError}
          </div>
        ) : null}

        <button
          onClick={handleLogin}
          disabled={loginBusy}
          style={{
            width: "100%",
            padding: "16px",
            background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "20px",
            fontWeight: "600",
            boxShadow: "0 8px 25px rgba(59, 130, 246, 0.4)",
            marginBottom: "10px",
            opacity: loginBusy ? 0.72 : 1,
          }}
        >
          {loginBusy
            ? t("login.signingIn", { defaultValue: "Вход…" })
            : legalReaccept
              ? t("login.acceptAndLogin", { defaultValue: "Принять и войти" })
              : t("login.btn_login", { defaultValue: "Войти" })}
        </button>

        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <button
            type="button"
            onClick={onRegister}
            style={{
              background: "none",
              border: "none",
              color: "#60a5fa",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            {t("login.fill_form")}
          </button>
        </div>

        {showOffer ? (
          <LegalDocsModal
            title={t("legal.driverOfferTitle", { defaultValue: "Оферта REISOR для водителя / перевозчика" })}
            url={offerUrl}
            onClose={() => setShowOffer(false)}
          />
        ) : null}

        {showPolicy ? (
          <LegalDocsModal
            title={t("legal.privacyTitle", { defaultValue: "Политика конфиденциальности REISOR" })}
            url={privacyUrl}
            onClose={() => setShowPolicy(false)}
          />
        ) : null}

        {reacceptDoc ? (
          <LegalDocsModal
            title={reacceptDoc.title || reacceptDoc.documentNumber || "REISOR"}
            url={legalDocumentUrl(reacceptDoc)}
            onClose={() => setReacceptDoc(null)}
          />
        ) : null}
      </div>
    </div>
  );
}
